/**
 * The rules behind the "Relevance" sort the Algolia-backed listings offer.
 *
 * Kept pure and out of the hook so the state machine — which is the whole of
 * what is interesting here — can be unit-tested without React.
 *
 * WHAT RELEVANCE IS ON THESE LISTINGS. None of them re-queries a sort replica
 * for it. Each already sends its typed query to the primary index (the brand and
 * calibre listings via `useBrandSearch` / `useCalibreListSearch`, the reference
 * listing via `searchReferences` with no `sort`), and the primary index is where
 * Algolia's ranking lives — custom ranking plus text match. The rank arrives
 * intact: `mergeBrandSearchResults` / `mergeCalibreSearchResults` emit hits in
 * hit order, and every filter downstream of them is a `.filter`, which preserves
 * it. Each listing then sorted that order away by name.
 *
 * So relevance is the ABSENCE of the local re-sort, not an extra query. Which is
 * also why it only exists while a query is typed: with an empty box there are no
 * hits, nothing was ranked, and "relevance" would mean whatever order the
 * index-wide fetch happened to return.
 */

/** The sentinel sort field. Not a column on any listing — see the module note. */
export const RELEVANCE_SORT = 'relevance';

/** A listing's own sort field, widened to admit the relevance sentinel. */
export type WithRelevance<F extends string> = F | typeof RELEVANCE_SORT;

/**
 * Is the listing actually showing ranked order right now?
 *
 * Both halves matter: the field can be `relevance` with the box empty (the user
 * picked it, then cleared their query — the pick is sticky), and in that state
 * there is no ranking to preserve, so the listing falls back to its default sort
 * via {@link effectiveSortField} rather than rendering raw index order.
 */
export function isRelevanceActive(sortField: string, query: string): boolean {
  return sortField === RELEVANCE_SORT && query.trim() !== '';
}

/**
 * The field a listing's comparator and column headers should read.
 *
 * Never the sentinel: comparators index rows by this field name, and `relevance`
 * is not a property of any row. Callers skip sorting entirely when
 * {@link isRelevanceActive}; this is what they use the rest of the time,
 * including the stale-pick case above.
 */
export function effectiveSortField<F extends string>(
  sortField: WithRelevance<F>,
  defaultSortField: F,
): F {
  return sortField === RELEVANCE_SORT ? defaultSortField : (sortField as F);
}

/**
 * The sort a listing should switch to for the query it is now showing, or `null`
 * to leave the current pick alone.
 *
 * The behaviour James locked (2026-08-06):
 *
 * - Empty box → whatever the listing defaults to today.
 * - A query is typed → switch to relevance, because a text match the admin can
 *   see ranked is the point of typing one.
 * - The admin picks a sort themselves → that pick STICKS for the session, and no
 *   later query auto-switches away from it. `userPicked` is the whole of that
 *   memory, which is why it is an input here rather than state.
 * - The box is cleared → back to the default, unless they overrode, in which
 *   case their pick is respected.
 *
 * Returning `null` rather than the unchanged field keeps the caller's effect
 * from setting state on every keystroke, which would re-run every memo hanging
 * off the sort field for a value that did not change.
 */
export function resolveQueryDrivenSort<F extends string>(params: {
  query: string;
  sortField: WithRelevance<F>;
  defaultSortField: F;
  userPicked: boolean;
}): WithRelevance<F> | null {
  const { query, sortField, defaultSortField, userPicked } = params;
  if (userPicked) return null;
  const hasQuery = query.trim() !== '';
  if (hasQuery) {
    return sortField === RELEVANCE_SORT ? null : RELEVANCE_SORT;
  }
  return sortField === RELEVANCE_SORT ? defaultSortField : null;
}
